import { useCallback, useEffect, useMemo } from "react";

import { useSmartOfferMessages } from "@/components/chat/ChatSmartOffer/useSmartOfferMessages";
import { usePlatformGuideManager } from "@/guides";
import { useProductSaleFlow } from "@/sales-tools/product-sale-flow/useProductSaleFlow";
import { getSessionByTaskId, useChatTasks, useLogger, useSessionId } from "@/sdk";
import { type ChatTask, useChatStore } from "@/sdk/agent/chat";
import { useWrapupTimer } from "@/sdk/sessions/hooks";
import { useSessionStore } from "@/sdk/sessions/session.store";
import { useFeatureFlag } from "@/shared-utils";
import { useSetupChatGaiaSubscriptions } from "@/timeline/state";

interface UseChatWorkspaceDataParams {
    /** Passed in so each consumer keeps its own `module` in logs (used by onChatSelected + the guide manager). */
    logger: ReturnType<typeof useLogger>;
}

/**
 * Single source of truth for the chat workspace's shared data orchestration, consumed by BOTH the
 * legacy `ChatManager` and the redesigned `ChatWorkspaceContentRedesigned`. Owns active-chat
 * resolution, chat selection, wrap-up scheduling, the chat feature flags, the guide manager, the
 * product-sale state, and runs the shared side effects (clear-active-chat, Gaia subscriptions, and
 * the smart-offer message hook). Layout-specific concerns (right-bottom vs right-top tab persistence,
 * the panel/tab-bar rendering) stay in each consumer.
 *
 * Only one consumer is mounted at a time (the `env.channel === "chat"` flag branch in
 * `WorkspaceContent`), so these effects run once.
 */
export function useChatWorkspaceData({ logger }: UseChatWorkspaceDataParams) {
    const chatTasks = useChatTasks();

    const activeChatId = useChatStore((state) => state.activeChatId);
    const setActiveChatId = useChatStore((state) => state.setActiveChatId);
    const clearActiveChatId = useChatStore((state) => state.clearActiveChatId);
    const { scheduleWrappingForTasks } = useWrapupTimer();
    const { selectedProduct, productSaleFlowPanelTitle, handleProductSaleClose } = useProductSaleFlow();

    const activeChatTask = useMemo(
        () => chatTasks.find((task) => task.chatId === activeChatId),
        [activeChatId, chatTasks],
    );

    useEffect(() => {
        if (activeChatId && !activeChatTask) {
            clearActiveChatId();
        }
    }, [activeChatId, activeChatTask, clearActiveChatId]);

    const onChatSelected = useCallback(
        (chat: ChatTask) => {
            if (chat.status === "pending") {
                logger.warn({ chatId: chat.chatId, taskId: chat.id }, "Attempted to select a pending chat");
                return;
            }

            const session = getSessionByTaskId(chat.id);
            if (session) {
                useSessionStore.getState().setSelectedSession(session.id);
            }
            setActiveChatId(chat.chatId);
        },
        [setActiveChatId, logger],
    );

    const isSelectedChat = useCallback(({ chatId }: ChatTask) => activeChatId === chatId, [activeChatId]);

    useEffect(() => {
        scheduleWrappingForTasks(chatTasks);
    }, [chatTasks, scheduleWrappingForTasks]);

    const { data: isSalesEnabled = false } = useFeatureFlag<boolean>("EWP-chat-isSalesEnabled", {
        partner: activeChatTask?.partner,
        topic: activeChatTask?.topic,
        language: activeChatTask?.language,
    });
    // Must match the seed-gate context in useChatExpertAssistSessions (partner + topic)
    const { data: isChatExpertAssistEnabled = false } = useFeatureFlag<boolean>("isChatExpertAssistEnabled", {
        partner: activeChatTask?.partner,
        topic: activeChatTask?.topic,
    });
    const { data: isChatSmartOfferFlowEnabled = false, isLoading: isSmartOfferFlagLoading } = useFeatureFlag<boolean>(
        "EWP-chat-isSmartOfferFlowEnabled",
        { partner: activeChatTask?.partner },
    );

    const canShowChatDetails = !!activeChatTask && activeChatTask.status !== "pending";
    const shouldShowQueue = chatTasks.length > 0;

    const sessionId = useSessionId();
    const { areGuidesAvailable, guide, guideToolboxCommand, isGuideOpen, closeGuide } = usePlatformGuideManager({
        isCustomerSession: !!activeChatTask,
        sessionId,
        logger,
    });

    useSetupChatGaiaSubscriptions(logger);

    useSmartOfferMessages(selectedProduct, isChatSmartOfferFlowEnabled);

    return {
        chatTasks,
        activeChatId,
        activeChatTask,
        onChatSelected,
        clearActiveChatId,
        isSelectedChat,
        canShowChatDetails,
        shouldShowQueue,
        isSalesEnabled,
        isChatExpertAssistEnabled,
        isChatSmartOfferFlowEnabled,
        isSmartOfferFlagLoading,
        areGuidesAvailable,
        guide,
        guideToolboxCommand,
        isGuideOpen,
        closeGuide,
        selectedProduct,
        productSaleFlowPanelTitle,
        handleProductSaleClose,
    };
}
